/**
 * device_extract.v1 — lightweight seized-device artefact list for Fusion Build (E3)
 * when a full UFDR/E01 isn't available. Real .ufdr/.e01 still go through forensic upload.
 */
import { canonicalEntityId, normalizeIdentifier } from '../entityJoin';
import type { GraphBag } from './ncrpParser';

export interface DeviceArtefact {
  type: 'wallet' | 'vpa' | 'phone' | 'handle' | string;
  value: string;
  note?: string;
}

export interface DeviceExtract {
  schema?: string;
  device?: { make?: string; model?: string; imei?: string; label?: string };
  artefacts?: DeviceArtefact[];
}

export interface DeviceParseResult {
  bags: GraphBag[];
  deviceNodeId: string;
  producedType?: string;
  producedNodeId?: string;
  summary: string;
}

const MODULE: Record<string, string> = {
  wallet: 'crypto',
  vpa: 'bank',
  phone: 'network',
  handle: 'social',
};

/** Parse device_extract.v1 JSON. Throws on bad input. */
export function parseDeviceExtract(raw: unknown): DeviceParseResult {
  let doc: DeviceExtract;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) throw new Error('Paste device_extract.v1 JSON or upload a UFDR/E01');
    try {
      doc = JSON.parse(t) as DeviceExtract;
    } catch {
      throw new Error('Invalid JSON — expected device_extract.v1');
    }
  } else if (raw && typeof raw === 'object') {
    doc = raw as DeviceExtract;
  } else {
    throw new Error('Expected device_extract.v1 JSON');
  }

  const schema = String(doc.schema || '').trim();
  if (schema && schema !== 'device_extract.v1') {
    throw new Error(`Unsupported schema "${schema}" — expected device_extract.v1`);
  }

  const device = doc.device || {};
  const label = (device.label
    || [device.make, device.model].filter(Boolean).join(' ')
    || device.imei
    || 'Seized device').trim();
  const deviceKey = normalizeIdentifier('device', device.imei || label) || label;
  const deviceNodeId = `ent:device:${deviceKey}`;

  const bags: GraphBag[] = [{
    id: deviceNodeId,
    label,
    module: 'shared',
    type: 'device',
    risk_score: 0.45,
    size: 7,
    step: 1,
    placement_role: 'source',
    make: device.make,
    model: device.model,
    imei: device.imei,
    attribution: { source: 'device_extract.v1', method: 'parse', confidence: 1 },
  }];

  const artefacts = Array.isArray(doc.artefacts) ? doc.artefacts : [];
  let producedType: string | undefined;
  let producedNodeId: string | undefined;
  let count = 0;

  for (const a of artefacts) {
    const type = String(a.type || '').trim().toLowerCase();
    const value = String(a.value || '').trim();
    if (!type || !value) continue;
    if (!['wallet', 'vpa', 'phone', 'handle'].includes(type)) continue;

    const id = canonicalEntityId(type, value);
    const display = type === 'phone'
      ? (normalizeIdentifier('phone', value) || value)
      : type === 'vpa' || type === 'handle'
        ? normalizeIdentifier(type, value)
        : value;

    if (!bags.some((b) => b.id === id)) {
      bags.push({
        id,
        label: display,
        module: MODULE[type] || 'shared',
        type,
        risk_score: type === 'wallet' ? 0.5 : 0.35,
        size: 4,
        step: 1,
        placement_role: 'context',
        note: a.note,
        attribution: { source: 'device_extract.v1', method: 'artefact', confidence: 1 },
      });
      count++;
    }

    bags.push({
      id: `device:edge:${deviceKey}~${id}`,
      label: type,
      module: 'shared',
      type: 'edge',
      source: deviceNodeId,
      target: id,
      edge_type: 'cross_link',
      determinism: 'deterministic',
      step: 1,
      _edgeLabel: `extracted ${type}`,
    });

    if (!producedNodeId) {
      producedType = type;
      producedNodeId = id;
    }
  }

  return {
    bags,
    deviceNodeId,
    producedType,
    producedNodeId,
    summary: `${label} · ${count} artefact(s)`,
  };
}

/** Heuristic: does this File look like device_extract.v1 JSON? */
export function isDeviceExtractJsonFile(file: File): boolean {
  const n = file.name.toLowerCase();
  return n.endsWith('.json') || file.type === 'application/json';
}

export function isForensicArchive(file: File): boolean {
  const n = file.name.toLowerCase();
  return /\.(ufdr|e01|zip|ab)$/i.test(n);
}
